<script setup lang="ts">
import RlClock from '@/components/common/RlClock.vue'
import DowntimeDialog from '@/components/eos/downtime/DowntimeDialog.vue'
import DailyDABidBulkUpdateDialog from '@/components/eos/table/DailyDABidBulkUpdateDialog.vue'
import EOSTable from '@/components/eos/table/EOSTable.vue'
import HourlyDABidDialog from '@/components/eos/table/HourlyDABidDialog.vue'
import RcSiteSelect, { type ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useEOSTable } from '@/composables/eos/eos-table'
import { useMisoNotifications } from '@/composables/eos/miso-notifications'
import { getLiveUpdates } from '@/services/eos/dashboard.service'
import { getSiteMapStructures } from '@/services/eos/site-map'
import { SiteType } from '@/types/core/Site'
import type { ISitePowerPositionForm } from '@/types/eos/forms'
import type { EOSTableRow, ISiteHourlyDABid } from '@/types/eos/Table'
import { formatMegawatts } from '@/util/formatters'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Edit, Money, PriceTag, Refresh, SwitchButton } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useIntervalFn, useTimeAgo } from '@vueuse/core'
import { ElMessage } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, h, ref, useTemplateRef, watch, watchEffect } from 'vue'

const selectedSite = ref<ISiteIdentifier>()
const selectedSiteId = computed(() => selectedSite.value?.site_id)
const siteSelectRef = useTemplateRef('siteSelectRef')

// Selected Luxon DateTime (and timezone)
const selectedTimezone = ref<string>('US/Central')
const selectedDateTime = ref<DateTime>(
  DateTime.fromObject({ hour: 0, minute: 0, second: 0, millisecond: 0 }, { zone: selectedTimezone.value })
)

// computed to proxy date from el-date-picker
const selectedDate = computed({
  get() {
    if (!selectedDateTime.value?.isValid) return undefined
    return selectedDateTime.value.toISO()!
  },
  set(value: string) {
    selectedDateTime.value = DateTime.fromFormat(value, 'yyyy-MM-dd', { zone: selectedTimezone.value, setZone: false })
  },
})

/**
 * Handles timezone change.
 *
 * @param {string} tz the new time zone.
 */
const handleTimezoneChange = (tz: string) => {
  selectedDateTime.value = selectedDateTime.value.setZone(tz, { keepLocalTime: true })
}

/**
 * Vue Query
 */
const queryClient = useQueryClient()

/**
 * Vue Query: EOS Table
 */
const {
  eosTableQuery,
  eosTableMonthlySummaryQuery,
  currentEnergyConsumptionQuery,
  tableNBPTQuery,
  siteDABidQuery,
  updateSitePowerPositionMutation,
} = useEOSTable()
const {
  isFetching: siteDABidQueryIsFetching,
  isError: siteDABidQueryIsError,
  data: siteDABid,
  refetch: refetchSiteDaBidQuery,
} = siteDABidQuery(selectedSiteId)

watch(
  () => siteDABidQueryIsError.value,
  (newValue) => {
    if (newValue) {
      ElMessage.error(`Failed to get site DA bid for ${selectedSiteId.value}.`)
    }
  }
)

/**
 * Vue Query: Current Energy Consumption
 */
const {
  isFetching: ceQueryIsFetching,
  isError: ceQueryIsError,
  data: ceQueryData,
} = currentEnergyConsumptionQuery(selectedSiteId)

/**
 * Vue Query: EOS Table
 */
const forceRefresh = ref(false)
const {
  isFetching: tableQueryIsFetching,
  isError: tableQueryIsError,
  data: tableQueryData,
  dataUpdatedAt: tableQueryDataUpdatedAt,
  error: tableQueryError,
  refetch: refetchTableQuery,
} = eosTableQuery(selectedSiteId, selectedDate, forceRefresh)

/**
 * Refetch table query with forceRefresh enabled
 */
const handleForceRefresh = async () => {
  forceRefresh.value = true
  try {
    await refetchTableQuery()
  } catch {
    // Error handling is done by Vue Query
  } finally {
    forceRefresh.value = false
  }
}

/**
 * Vue Query: EOS Table Summary (TODO: needs some work, should probably consolidate)
 */
const { isError: tableSummaryQueryIsError, data: tableSummaryQueryData } = eosTableMonthlySummaryQuery(
  selectedSiteId,
  selectedDateTime,
  selectedTimezone
)

watchEffect(() => {
  if (tableSummaryQueryIsError.value) {
    ElMessage.error('Failed to fetch table monthly summary.')
  }
})

const loadFactor = computed(() => tableSummaryQueryData.value?.load_factor)
const nonCoincidentPeakData = computed(() => tableSummaryQueryData.value?.non_coincident_peak)

/**
 * Vue Query: MISO Notifications
 */
const { misoNotificationsQuery } = useMisoNotifications()
const { isError: misoNotificationsQueryIsError, data: misoNotifications } = misoNotificationsQuery(
  selectedSiteId,
  selectedDateTime
)

watchEffect(() => {
  if (misoNotificationsQueryIsError.value) {
    ElMessage.error('Failed to fetch MISO notifications.')
  }
})

/**
 * Vue Query: NBPT
 */
const {
  isFetching: tableNBPTQueryIsFetching,
  isError: tableNBPTQueryIsError,
  data: tableNBPTQueryData,
} = tableNBPTQuery(selectedDateTime)

watchEffect(() => {
  if (tableNBPTQueryIsError.value) {
    ElMessage.error('Failed to retrieve NBPT.')
  }
})

const nbptDisplayValue = computed(() => {
  if (tableNBPTQueryIsFetching.value) return 'Loading...'
  if (tableNBPTQueryIsError.value) return 'Failed to load NBPT.'
  return tableNBPTQueryData.value ?? 'No NBPT specified.'
})

// Last updated
const lastUpdatedAgo = useTimeAgo(tableQueryDataUpdatedAt)

// Table state
const tableHasData = computed(() => !!selectedSiteId.value && !!selectedDate.value)
const tableEmptyText = computed(() => {
  if (tableQueryIsFetching.value || siteSelectRef.value?.isFetching) {
    return 'Loading...'
  } else if (!!selectedSiteId.value && !selectedDate.value) {
    return 'Please select a date.'
  } else if (!!selectedDate.value && !selectedSiteId.value) {
    return 'Please select a site.'
  }
  return 'No data.'
})

/**
 * Live RT LMP updates
 */
const { pause, resume } = useIntervalFn(async () => {
  if (!selectedSiteId.value) return
  try {
    ElMessage.info('Pulling live update')
    const liveUpdates = await getLiveUpdates(selectedSiteId.value)
    queryClient.setQueryData(['eos', 'table', selectedSiteId, selectedDateTime], (oldData: EOSTableRow[]) =>
      oldData?.map((row) => {
        if (row.hour === liveUpdates.current_hour_timestamp) {
          return { ...row, rt_lmp: liveUpdates.rt_lmp, live_update: true } // overwrites rt_lmp for the current hour, adds flag
        } else {
          delete row.live_update // remove previous live update if we're spanning two hours
          return row
        }
      })
    )
  } catch {
    ElMessage.error({ message: 'Failed to fetch latest RT LMP value.', showClose: true, duration: 3500 })
  }
}, 300_000) // 5 minutes

watchEffect(() => {
  // we're keeping local time so we retain the same date
  const today = DateTime.now().setZone(selectedTimezone.value, { keepLocalTime: true }).startOf('day')
  const shouldPause =
    tableQueryIsError.value ||
    tableQueryIsFetching.value ||
    !selectedSiteId.value ||
    !selectedDateTime.value ||
    !selectedDateTime.value.equals(today)
  if (shouldPause) {
    pause()
  } else {
    resume()
  }
})

/**
 * Site Downtime
 */
const downtimeDialogVisible = ref<boolean>(false)
const downtimeDialogSelectedStartHour = ref<string>()

const openDowntimeDialog = async (row: EOSTableRow) => {
  downtimeDialogSelectedStartHour.value = row.hour
  downtimeDialogVisible.value = true
}

const handleDowntimeCreate = () => {
  queryClient.invalidateQueries({ queryKey: ['eos', 'table'] })
}

/**
 * Hourly DA Bid
 */
const hourlyDABidDialogVisible = ref<boolean>(false)
const hourlyDABidDialogCurrentValue = ref<ISiteHourlyDABid>()

/**
 * Handles hourly DA bid dialog open.
 * Extracts current bid from row, opens dialog.
 *
 * @param {EOSTableRow} row the row to target for create or update.
 */
const openHourlyDABidDialog = (row: EOSTableRow) => {
  const currentHourlyDABid: ISiteHourlyDABid = {
    site_id: selectedSiteId.value!,
    for_hour_utc: row.hour,
    price_usd: row.da_price_bid,
    load_mw: row.da_load_bid,
    pr_strike_price_usd: row.pr_strike_price_usd,
    pr_load_mw: row.pr_load_mw,
    energy_market_non_curtailable_load_mw: row.energy_market_non_curtailable_load_mw,
    ancillary_services_market_non_curtailable_load_mw: row.ancillary_services_market_non_curtailable_load_mw,
  }

  hourlyDABidDialogCurrentValue.value = currentHourlyDABid
  hourlyDABidDialogVisible.value = true
}

const handleHourlyDABidUpdate = () => {
  queryClient.invalidateQueries({ queryKey: ['eos', 'table'] })
}

const handleRowUpdated = (sitePowerPositionForm: ISitePowerPositionForm) => {
  if (!selectedSiteId.value || !selectedDate.value) return
  updateSitePowerPositionMutation.mutate({
    siteID: selectedSiteId.value,
    form: sitePowerPositionForm,
    dateTime: selectedDate.value,
  })
}

const handleBulkLowUptimeUpdate = (sitePowerPositionForm: ISitePowerPositionForm) => {
  if (!selectedSiteId.value || !selectedDate.value) return
  updateSitePowerPositionMutation.mutate({
    siteID: selectedSiteId.value,
    form: sitePowerPositionForm,
    dateTime: selectedDate.value,
  })
}

/**
 * Site DA Bid
 */
const siteDABidDialogOpen = ref<boolean>(false)

/**
 * Hourly DA Bid Bulk Update
 */
const hourlyDABidBulkUpdateDialogOpen = ref<boolean>(false)

/**
 * Check if site has structures (disable downtime button if not).
 */

/**
 * Handles site changed event.
 *
 * On site change, checks if a site has structures.
 * If it doesn't, disables "Add Downtime" dialog trigger button.
 *
 * @param {ISiteIdentifer | undefined} val the newly-selected site.
 */
const onSiteChange = async (val?: ISiteIdentifier) => {
  if (!val) return
  try {
    siteStructureCheckFailed.value = false
    siteStructureCheckLoading.value = true
    const { data } = await getSiteMapStructures({ site_id: val.site_id, page: 1, per_page: 1 })
    if (data.length > 0) {
      siteHasStructures.value = true
    } else {
      siteHasStructures.value = false
    }
  } catch {
    siteHasStructures.value = false
    siteStructureCheckFailed.value = true
    ElMessage.warning('Failed to check site for structures, downtime will not be available.')
  } finally {
    siteStructureCheckLoading.value = false
  }
}
const siteHasStructures = ref<boolean>(false)
const siteStructureCheckLoading = ref<boolean>(false)
const siteStructureCheckFailed = ref<boolean>(false)

// Watch for selectedSite changes and call onSiteChange when it changes
// Change property cannot be used becaue it's not called when the component is mounted
watch(
  selectedSite,
  (newSite) => {
    if (newSite) {
      onSiteChange(newSite)
    }
  },
  { immediate: true }
)
</script>

<template>
  <ViewLayout>
    <template #controls>
      <template v-if="!tableQueryIsError">
        <el-button
          :icon="Refresh"
          type="primary"
          class="actions-button"
          :loading="tableQueryIsFetching"
          @click="handleForceRefresh"
          >Force Refresh</el-button
        >
        <el-tooltip v-if="selectedSiteId" :disabled="!siteDABidQueryIsError">
          <template #default>
            <el-button
              :icon="Edit"
              type="primary"
              class="actions-button"
              :loading="siteDABidQueryIsFetching || tableQueryIsFetching"
              :disabled="siteDABidQueryIsError"
              @click="siteDABidDialogOpen = true"
              >Edit Site DA Bid</el-button
            >
          </template>
          <template #content>
            <div class="inline-flex items-center gap-2">
              Failed to fetch existing site DA bid.
              <el-button
                type="primary"
                size="small"
                :loading="siteDABidQueryIsFetching"
                :disabled="!siteDABidQueryIsError"
                @click="refetchSiteDaBidQuery()"
                >Retry?</el-button
              >
            </div>
          </template>
        </el-tooltip>
        <el-button
          v-if="!tableQueryIsError && selectedSiteId && selectedDate"
          :icon="PriceTag"
          type="primary"
          class="actions-button"
          :loading="tableQueryIsFetching"
          @click="hourlyDABidBulkUpdateDialogOpen = true"
        >
          Daily DA Bid Bulk Update
        </el-button>
      </template>
      <RlClock :timezone="selectedTimezone" />
    </template>

    <div class="flex flex-col gap-xs">
      <div class="flex flex-col md:flex-row justify-between gap-xs items-center">
        <div class="flex flex-row gap-xs">
          <RcSiteSelect
            ref="siteSelectRef"
            v-model="selectedSite"
            class="w-64"
            :type="SiteType.DataCenter"
            clearable
            default-first-remote-option
          />
          <RlTimezoneSelect
            v-if="!tableQueryIsError"
            v-model="selectedTimezone"
            class="w-56"
            @change="handleTimezoneChange"
          />
          <el-date-picker
            v-if="!tableQueryIsError"
            v-model="selectedDate"
            class="w-48!"
            type="date"
            placeholder="Pick a day"
            value-format="YYYY-MM-DD"
          />
        </div>
        <div v-if="!tableQueryIsError" class="flex flex-row gap-xs">
          <div v-if="tableHasData" class="flex flex-col">
            <el-descriptions size="small" border>
              <el-descriptions-item>
                <template #label>
                  <div class="inline-flex items-center gap-xs">
                    <el-tooltip effect="light" placement="bottom-start" :disabled="!nonCoincidentPeakData">
                      Non Coincident Peak
                      <template #content>
                        <div v-if="nonCoincidentPeakData?.timestamp">
                          {{
                            DateTime.fromISO(nonCoincidentPeakData.timestamp)
                              .setZone(selectedTimezone)
                              .toFormat('yyyy-MM-dd')
                          }}
                          - HE
                          {{
                            String(
                              Math.min(
                                DateTime.fromISO(nonCoincidentPeakData.timestamp).setZone(selectedTimezone).hour + 1,
                                24
                              )
                            ).padStart(2, '0')
                          }}
                        </div>
                      </template>
                      <Icon class="cursor-help" icon="material-symbols:info-outline-rounded" width="13" height="13" />
                    </el-tooltip>
                  </div>
                </template>
                <template v-if="!tableSummaryQueryData">
                  <Icon icon="eos-icons:three-dots-loading" />
                </template>
                <template v-else>
                  <div>{{ nonCoincidentPeakData?.load_mwh ?? '-' }}</div>
                </template>
              </el-descriptions-item>
            </el-descriptions>
          </div>
          <div v-if="tableHasData" class="flex flex-col">
            <el-descriptions size="small" border>
              <el-descriptions-item>
                <template #label>
                  <span class="inline-flex items-center gap-xs">Load Factor</span>
                </template>
                <template v-if="!tableSummaryQueryData">
                  <Icon icon="eos-icons:three-dots-loading" />
                </template>
                <template v-else>
                  <div>{{ loadFactor }}</div>
                </template>
              </el-descriptions-item>
            </el-descriptions>
          </div>
          <div v-if="selectedSiteId" class="flex flex-col">
            <el-descriptions size="small" border>
              <el-descriptions-item>
                <template #label>
                  <div class="inline-flex items-center gap-xs">
                    <el-tooltip effect="light" placement="bottom-start" :disabled="ceQueryIsFetching">
                      Current Energy Consumption (MWh)
                      <template #content>
                        <div v-if="ceQueryData">
                          As of
                          {{
                            DateTime.fromISO(ceQueryData?.last_measure_time)
                              .setZone(selectedTimezone)
                              .toFormat('yyyy-MM-dd HH:mm:ss ZZZZ')
                          }}
                        </div>
                        <span v-if="ceQueryIsError" class="text-orange-600"
                          >Failed to fetch current energy consumption for {{ selectedSiteId }}.</span
                        >
                      </template>
                      <Icon
                        v-if="ceQueryIsError"
                        class="cursor-help text-orange-600"
                        icon="material-symbols:error-outline-rounded"
                        width="13"
                        height="13"
                      />
                      <Icon
                        v-else
                        class="cursor-help"
                        icon="material-symbols:info-outline-rounded"
                        width="13"
                        height="13"
                      />
                    </el-tooltip>
                  </div>
                </template>
                <template v-if="ceQueryIsFetching || tableQueryIsFetching">
                  <Icon icon="eos-icons:three-dots-loading" />
                </template>
                <div v-else-if="ceQueryIsError && !ceQueryData">-</div>
                <template v-else-if="ceQueryData">
                  {{ formatMegawatts(ceQueryData?.rt_kw_incoming / 1000) }}
                  <span v-if="ceQueryIsError">(stale)</span>
                </template>
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </div>
      </div>
      <RlErrorResult v-if="tableQueryIsError" :error="tableQueryError">
        <div class="inline-flex items-center">
          <el-button
            type="primary"
            :icon="() => h(Icon, { icon: 'material-symbols:refresh', height: 20, inline: true })"
            @click="refetchTableQuery()"
            >Try again?</el-button
          >
          <el-button link @click="siteSelectRef?.focus()">or, select another site</el-button>
        </div>
      </RlErrorResult>
      <EOSTable
        v-else
        v-loading="tableQueryIsFetching"
        :data="tableHasData ? tableQueryData : []"
        :show-monthly-summary="!tableSummaryQueryIsError"
        :monthly-summary="tableSummaryQueryData"
        :miso-notifications="misoNotifications"
        :timezone="selectedTimezone"
        :empty-text="tableEmptyText"
        @row-updated="handleRowUpdated"
        @bulk-low-uptime-update="handleBulkLowUptimeUpdate"
      >
        <template #actions-column="scope">
          <el-tooltip
            :disabled="siteHasStructures && !tableQueryIsFetching && !siteStructureCheckLoading"
            :content="siteStructureCheckFailed ? 'Failed to get site structures.' : 'Site has no structures.'"
          >
            <el-button
              link
              :icon="SwitchButton"
              :disabled="!siteHasStructures"
              @click="openDowntimeDialog(scope.row as EOSTableRow)"
            />
          </el-tooltip>
          <el-button link :icon="Money" @click="openHourlyDABidDialog(scope.row as EOSTableRow)" />
        </template>
      </EOSTable>
      <template v-if="!tableQueryIsError && tableHasData">
        <small v-if="tableQueryIsFetching" class="self-end">Updating...</small>
        <small v-else class="self-end">Last updated {{ lastUpdatedAgo }}</small>
      </template>
    </div>
    <HourlyDABidDialog
      v-if="selectedSite"
      v-model="hourlyDABidDialogVisible"
      :site="selectedSite"
      :timezone="selectedTimezone"
      :current-hourly-bid="hourlyDABidDialogCurrentValue"
      @update="handleHourlyDABidUpdate"
    />
    <DowntimeDialog
      v-if="selectedSite"
      v-model="downtimeDialogVisible"
      :start-datetime="downtimeDialogSelectedStartHour"
      :tz="selectedTimezone"
      :site="selectedSite"
      @closed="downtimeDialogSelectedStartHour = undefined"
      @create="handleDowntimeCreate"
    />
    <SiteDABidDialog
      v-if="selectedSite"
      v-model="siteDABidDialogOpen"
      :site="selectedSite"
      :site-da-bid="siteDABid"
      :nbpt="nbptDisplayValue"
    />
    <DailyDABidBulkUpdateDialog
      v-if="selectedSite && selectedDate"
      v-model="hourlyDABidBulkUpdateDialogOpen"
      :site="selectedSite"
      :date-time="selectedDateTime"
      :nbpt="nbptDisplayValue"
    />
  </ViewLayout>
</template>

<style scoped lang="scss">
.actions-button + .actions-button {
  margin-left: 0;
}
</style>
